/**
 * NWS (National Weather Service) Map Layer Configuration
 *
 * All layers are ArcGIS MapServer services from mapservices.weather.noaa.gov
 * Reference: https://www.weather.gov/gis/cloudgiswebservices
 */

export interface NWSLayerConfig {
  id: string;
  name: string;
  url: string;
  description: string;
  defaultVisible: boolean;
  opacity?: number;
  externalUrl?: string;  // Base URL for "View on X" link
  externalLabel?: string; // Label for external link (e.g., "View on NWS")
}

export interface NWSLayerGroup {
  id: string;
  name: string;
  icon: string;
  color: string;
  defaultExpanded: boolean;
  layers: NWSLayerConfig[];
}

// ============================================================================
// NWS LAYER DEFINITIONS
// ============================================================================

export const NWS_LAYER_GROUPS: NWSLayerGroup[] = [
  {
    id: 'alerts',
    name: 'Watches, Warnings & Advisories',
    icon: 'alert',
    color: '#dc2626', // red
    defaultExpanded: true,
    layers: [
      {
        id: 'wwa',
        name: 'Watches/Warnings/Advisories',
        url: 'https://mapservices.weather.noaa.gov/eventdriven/rest/services/WWA/watch_warn_adv/MapServer',
        description: 'All active NWS watches, warnings, and advisories',
        defaultVisible: true,
        opacity: 0.7,
        externalUrl: 'https://www.weather.gov/alerts',
        externalLabel: 'View on NWS',
      },
    ],
  },
  {
    id: 'tropical',
    name: 'Tropical Weather',
    icon: 'hurricane',
    color: '#f97316', // orange
    defaultExpanded: false,
    layers: [
      {
        id: 'nhc_tropical',
        name: 'NHC Tropical Weather',
        url: 'https://mapservices.weather.noaa.gov/tropical/rest/services/tropical/NHC_tropical_weather/MapServer',
        description: 'Tropical cyclone tracks, forecasts, and wind radii',
        defaultVisible: false,
        externalUrl: 'https://www.nhc.noaa.gov',
        externalLabel: 'View on NHC',
      },
      {
        id: 'nhc_storm_surge',
        name: 'Peak Storm Surge',
        url: 'https://mapservices.weather.noaa.gov/tropical/rest/services/tropical/NHC_PeakStormSurge/MapServer',
        description: 'Potential storm surge inundation areas',
        defaultVisible: false,
        externalUrl: 'https://www.nhc.noaa.gov/surge',
        externalLabel: 'View on NHC',
      },
      {
        id: 'nhc_summary',
        name: 'Tropical Summary',
        url: 'https://mapservices.weather.noaa.gov/tropical/rest/services/tropical/NHC_tropical_weather_summary/MapServer',
        description: 'Tropical weather summary and outlook areas',
        defaultVisible: false,
        externalUrl: 'https://www.nhc.noaa.gov',
        externalLabel: 'View on NHC',
      },
    ],
  },
  {
    id: 'outlooks',
    name: 'Severe Weather Outlooks',
    icon: 'storm',
    color: '#eab308', // yellow
    defaultExpanded: false,
    layers: [
      {
        id: 'spc_outlooks',
        name: 'SPC Convective Outlooks',
        url: 'https://mapservices.weather.noaa.gov/vector/rest/services/outlooks/SPC_wx_outlks/MapServer',
        description: 'Storm Prediction Center convective outlooks (Day 1-8)',
        defaultVisible: false,
        opacity: 0.6,
        externalUrl: 'https://www.spc.noaa.gov/products/outlook',
        externalLabel: 'View on SPC',
      },
      {
        id: 'spc_meso',
        name: 'Mesoscale Discussions',
        url: 'https://mapservices.weather.noaa.gov/vector/rest/services/outlooks/spc_mesoscale_discussion/MapServer',
        description: 'SPC mesoscale discussions for developing severe weather',
        defaultVisible: false,
        externalUrl: 'https://www.spc.noaa.gov/products/md',
        externalLabel: 'View on SPC',
      },
    ],
  },
  {
    id: 'hazards',
    name: 'Weather Hazards',
    icon: 'hazard',
    color: '#7c3aed', // purple
    defaultExpanded: false,
    layers: [
      {
        id: 'wpc_precip',
        name: 'Precipitation Hazards',
        url: 'https://mapservices.weather.noaa.gov/vector/rest/services/hazards/wpc_precip_hazards/MapServer',
        description: 'WPC excessive rainfall and flash flood outlooks',
        defaultVisible: false,
        externalUrl: 'https://www.wpc.ncep.noaa.gov/qpf/excessive_rainfall_outlook_ero.php',
        externalLabel: 'View on WPC',
      },
      {
        id: 'winter_precip',
        name: 'Winter Precipitation Probability',
        url: 'https://mapservices.weather.noaa.gov/vector/rest/services/precip/wpc_prob_winter_precip/MapServer',
        description: 'Probability of winter precipitation types',
        defaultVisible: false,
        externalUrl: 'https://www.wpc.ncep.noaa.gov',
        externalLabel: 'View on WPC',
      },
      {
        id: 'cpc_hazards',
        name: 'CPC Weather Hazards',
        url: 'https://mapservices.weather.noaa.gov/vector/rest/services/hazards/cpc_weather_hazards/MapServer',
        description: 'Climate Prediction Center weather hazard outlooks',
        defaultVisible: false,
        externalUrl: 'https://www.cpc.ncep.noaa.gov/products/predictions/threats/threats.php',
        externalLabel: 'View on CPC',
      },
    ],
  },
  {
    id: 'fire',
    name: 'Fire Weather',
    icon: 'fire',
    color: '#ef4444', // red
    defaultExpanded: false,
    layers: [
      {
        id: 'spc_fire',
        name: 'SPC Fire Weather Outlook',
        url: 'https://mapservices.weather.noaa.gov/vector/rest/services/fire_weather/SPC_firewx/MapServer',
        description: 'Fire weather outlooks from Storm Prediction Center',
        defaultVisible: false,
        opacity: 0.6,
        externalUrl: 'https://www.spc.noaa.gov/products/fire_wx',
        externalLabel: 'View on SPC',
      },
      {
        id: 'fire_spot',
        name: 'Spot Fire Forecasts',
        url: 'https://mapservices.weather.noaa.gov/vector/rest/services/fire_weather/nws_fire_weather_spot/MapServer',
        description: 'NWS spot fire weather forecast locations',
        defaultVisible: false,
        externalUrl: 'https://www.weather.gov/fire',
        externalLabel: 'View on NWS',
      },
    ],
  },
  {
    id: 'observations',
    name: 'Observations & Reports',
    icon: 'observation',
    color: '#3b82f6', // blue
    defaultExpanded: false,
    layers: [
      {
        id: 'surface_obs',
        name: 'Surface Observations',
        url: 'https://mapservices.weather.noaa.gov/vector/rest/services/obs/surface_obs/MapServer',
        description: 'Current surface weather observations from stations',
        defaultVisible: false,
        externalUrl: 'https://www.weather.gov',
        externalLabel: 'View on NWS',
      },
      {
        id: 'storm_reports',
        name: 'Local Storm Reports',
        url: 'https://mapservices.weather.noaa.gov/vector/rest/services/obs/nws_local_storm_reports/MapServer',
        description: 'Preliminary local storm reports (hail, wind, tornadoes)',
        defaultVisible: false,
        externalUrl: 'https://www.spc.noaa.gov/climo/reports/today.html',
        externalLabel: 'View on SPC',
      },
    ],
  },
  {
    id: 'radar',
    name: 'Radar',
    icon: 'radar',
    color: '#06b6d4', // cyan
    defaultExpanded: true,
    layers: [
      {
        id: 'nexrad',
        name: 'NEXRAD Radar',
        url: 'https://mesonet.agron.iastate.edu/cache/tile.py/1.0.0/nexrad-n0q-900913/{z}/{x}/{y}.png',
        description: 'NEXRAD base reflectivity composite',
        defaultVisible: false,
        opacity: 0.7,
      },
    ],
  },
  {
    id: 'flood-zones',
    name: 'Flood Zones (FEMA NFHL)',
    icon: 'water',
    color: '#3b82f6', // blue
    defaultExpanded: false,
    layers: [
      {
        id: 'fema_nfhl_sfha',
        name: 'Special Flood Hazard Areas',
        url: 'https://hazards.fema.gov/arcgis/rest/services/public/NFHL/MapServer',
        description: 'FEMA National Flood Hazard Layer — 100-year and 500-year flood zones',
        defaultVisible: false,
        opacity: 0.5,
        externalUrl: 'https://msc.fema.gov/portal/home',
        externalLabel: 'View on FEMA MSC',
      },
    ],
  },
  // NOTE: The "Flood Inundation Maps (NWS FIM)" group used to live here. Its
  // layers were consolidated into the single "Flood Inundation" section of the
  // layer menu (rendered in LayerControl.tsx alongside our modeled extents) so
  // users no longer hunt across Weather vs Operations for flood layers.
  //   - `nws_fim_catfim` was extracted to the NWS_FIM_CATFIM_LAYER const below
  //     and is rendered inside that consolidated section.
  //   - `nws_fim_realtime` (NWM analysis-based "Real-Time Inundation Extent")
  //     was DROPPED entirely: it is a duplicate of our own
  //     `flood_inundation_analysis` operational layer (same National Water Model
  //     analysis-based inundation extent), so surfacing both only confused users.
  {
    id: 'precip-forecast',
    name: 'Precipitation Forecast / ERO',
    icon: 'cloud-rain',
    color: '#8b5cf6', // purple
    defaultExpanded: false,
    layers: [
      {
        id: 'wpc_qpf',
        name: 'Quantitative Precipitation Forecast',
        url: 'https://mapservices.weather.noaa.gov/vector/rest/services/precip/wpc_qpf/MapServer',
        description: 'WPC Quantitative Precipitation Forecast & Excessive Rainfall Outlook (1-7 days)',
        defaultVisible: false,
        opacity: 0.5,
        externalUrl: 'https://www.wpc.ncep.noaa.gov/qpf/qpf2.shtml',
        externalLabel: 'View on WPC',
      },
    ],
  },
];

/**
 * NWS CatFIM reference layer — categorical (gauge-based) flood inundation.
 * Extracted from the former `flood-inundation` NWS group so it can be rendered
 * inside the consolidated "Flood Inundation" menu section (LayerControl.tsx)
 * next to our modeled extents. It is still keyed by id `nws_fim_catfim` in the
 * nwsLayerVisibility store + popups + legend, so its wiring is unchanged.
 */
export const NWS_FIM_CATFIM_LAYER: NWSLayerConfig = {
  id: 'nws_fim_catfim',
  name: 'Categorical Flood Inundation (CatFIM)',
  url: 'https://maps.water.noaa.gov/server/rest/services/fim_libs/static_stage_based_catfim/MapServer',
  description: 'Pre-computed flood extents by severity category (action, minor, moderate, major) at NWS forecast points. Categorical/coarse — asset-level impacts may be over- or under-stated; verify in the field.',
  defaultVisible: false,
  opacity: 0.6,
  externalUrl: 'https://water.noaa.gov/tools/floodinundation',
  externalLabel: 'View on NOAA Water',
};

// Flatten all layers for easy lookup. Includes the extracted CatFIM layer so
// default visibility, identify priority, popups, and legend lookups still work.
export const ALL_NWS_LAYERS: NWSLayerConfig[] = [
  ...NWS_LAYER_GROUPS.flatMap(g => g.layers),
  NWS_FIM_CATFIM_LAYER,
];

// Get layer by ID
export function getNWSLayerById(id: string): NWSLayerConfig | undefined {
  return ALL_NWS_LAYERS.find(l => l.id === id);
}

// Get all layer IDs
export function getAllNWSLayerIds(): string[] {
  return ALL_NWS_LAYERS.map(l => l.id);
}

// Get default visibility state
export function getDefaultNWSLayerVisibility(): Record<string, boolean> {
  const visibility: Record<string, boolean> = {};
  ALL_NWS_LAYERS.forEach(layer => {
    visibility[layer.id] = layer.defaultVisible;
  });
  return visibility;
}
